import { useRouter } from 'next/router'
import { useCallback } from 'react'

import { useAnalytics } from '@flecto/analytics'
import {
  useMutationApproveInquiry,
  useMutationRejectInquiry,
  useMutationUnapproveInquiry,
  useMutationUnrejectInquiry,
} from '@flecto/core/saas'
import { useQueryGetUserLogged } from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import { type UNSAFE_any, type CoreInquiry } from '@flecto/types'
import { useNotifications } from '@flecto/ui'
import formatInTimezone from '@flecto/utils/src/Date/formatInTimezone'
import isSafeDate from '@flecto/utils/src/Date/isSafeDate'

import { useModalRejectInquiry } from '../components/ModalRejectInquiry/useModalRejectInquiry'
import { useModalReopenInquiry } from '../components/ModalReopenInquiry/useModalReopenInquiry'

export type UseInquiryActionsOptions = {
  inquiry?: CoreInquiry
}

export function useInquiryActions({ inquiry }: UseInquiryActionsOptions) {
  const { t } = useTranslation('saas', { keyPrefix: 'inquiries' })
  const track = useAnalytics('segment')
  const router = useRouter()

  const { data: user } = useQueryGetUserLogged()

  const { mutateAsync: approveInquiry } = useMutationApproveInquiry()
  const { mutateAsync: unapproveInquiry } = useMutationUnapproveInquiry()
  const { mutateAsync: rejectInquiry } = useMutationRejectInquiry()
  const { mutateAsync: unrejectInquiry } = useMutationUnrejectInquiry()

  const { showNotification } = useNotifications()

  const { openModal: openRejectModal } = useModalRejectInquiry()

  const { openModal: openReopenModal } = useModalReopenInquiry()

  const formattedStartDate = () => {
    if (isSafeDate(inquiry?.start_at)) {
      return formatInTimezone(
        new Date(inquiry?.start_at ?? ''),
        'dd/MM/yyyy HH:mm'
      )
    }
  }

  const handleError = (err: UNSAFE_any) => {
    const message = err?.response?.data?.errors?.[0].detail ?? ''

    showNotification({ type: 'error', message })
  }

  const handleApprove = useCallback(async () => {
    try {
      const inquiryId = inquiry?.internal_id ?? 0
      const userId = user?.id ?? 0

      await approveInquiry({
        inquiryId,
        options: { send_email: true },
      })

      track('CLICK_TO__CONVERT_LEAD', {
        lead_id: inquiryId,
        owner_id: userId,
      })

      showNotification({
        type: 'success',
        message: t('notifications.approve_success', {
          replace: { id: inquiry?.booking?.internal_id },
        }),
      })
    } catch (err) {
      handleError(err as Error)
    }
  }, [inquiry, user])

  const handleUnapprove = useCallback(async () => {
    const inquiryId = inquiry?.internal_id ?? 0
    const userId = user?.id ?? 0

    try {
      await unapproveInquiry({ inquiryId })

      track('LEAD__CLICK_TO__REOPEN', {
        lead_id: inquiryId,
        owner_id: userId,
      })

      showNotification({
        type: 'success',
        message: t('notifications.reopen_success', {
          replace: {
            date: formattedStartDate(),
            id: inquiryId,
          },
        }),
      })
    } catch (err) {
      handleError(err as Error)
    }
  }, [inquiry, user])

  const handleReject = useCallback(
    async (sendEmail: boolean) => {
      const inquiryId = inquiry?.internal_id ?? 0
      const userId = user?.id ?? 0

      try {
        await rejectInquiry({
          inquiryId,
          options: { send_email: sendEmail },
        })
        track('LEAD__CLICK_TO__REJECT', {
          lead_id: inquiryId,
          owner_id: userId,
        })
        showNotification({
          type: 'success',
          message: t('notifications.reject_success'),
        })
      } catch (err) {
        handleError(err as Error)
      }
    },
    [inquiry, user]
  )

  const handleUnreject = useCallback(async () => {
    try {
      const inquiryId = inquiry?.internal_id ?? 0
      const userId = user?.id ?? 0

      await unrejectInquiry({ inquiryId })

      track('LEAD__CLICK_TO__REOPEN', {
        lead_id: inquiryId,
        owner_id: userId,
      })

      showNotification({
        type: 'success',
        message: t('notifications.reopen_success', {
          replace: {
            date: formattedStartDate(),
            id: inquiryId,
          },
        }),
      })
    } catch (e) {
      handleError(e as Error)
    }
  }, [inquiry, user])

  const handleReopen = useCallback(() => {
    if (inquiry?.status === 'rejected') {
      handleUnreject()
    } else if (inquiry?.status === 'approved') {
      handleUnapprove()
    }
  }, [inquiry])

  const handleOpenBooking = useCallback(() => {
    router.push(`/bookings/${inquiry?.booking?.internal_id}`)
  }, [inquiry])

  return {
    handleApprove,
    handleReject,
    handleReopen,
    handleUnreject,
    handleOpenBooking,
    handleUnapprove,
    openRejectModal,
    openReopenModal,
    inquiryStatus: inquiry?.status,
    bookingId: inquiry?.booking?.internal_id,
    userId: user?.id,
  }
}
